import type { RefCallback } from "react";
import { useCallback, useEffect, useRef } from "react";

export default function useInfiniteScroll({
  onIntersect,
  rootMargin = "0px",
}: {
  onIntersect: () => unknown;
  rootMargin?: string;
}): RefCallback<HTMLElement> {
  const isFetchingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const nodeRef = useRef<HTMLElement | null>(null);

  const onIntersectRef = useRef(onIntersect);
  onIntersectRef.current = onIntersect;

  const handleObserver = async ([entry]: IntersectionObserverEntry[]) => {
    if (!entry.isIntersecting || isFetchingRef.current) return;

    isFetchingRef.current = true;

    try {
      await onIntersectRef.current();
    } finally {
      isFetchingRef.current = false;
    }
  };

  const loaderElement = useCallback<RefCallback<HTMLElement>>(
    (node) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      nodeRef.current = node;

      if (!node) return;

      const observer = new IntersectionObserver(handleObserver, { root: null, rootMargin, threshold: 0 });

      observer.observe(node);
      observerRef.current = observer;
    },
    [rootMargin],
  );

  useEffect(() => {
    if (!nodeRef.current || !observerRef.current) return;

    observerRef.current.unobserve(nodeRef.current);
    observerRef.current.observe(nodeRef.current);
  }, []);

  return loaderElement;
}
